import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { HubConnection } from "@microsoft/signalr";

import { useSelector } from "react-redux";
import {
  SignalRMessage,
  SignalRStatusEnum,
  SignalRTypeEnum,
} from "../components/common/signal-r-popup/types";
import { EldFileExportDto } from "../common/data/service-proxies";
import { ExportTypes } from "../constants/ExportImportTypes";
import { useRefresh } from "ra-core";
import { AppState } from "../common/types";

type ReportResultDto = {
  success: boolean;
  name: string | null | undefined;
  reportUrl: string | null | undefined;
  request: unknown;
  reportId: string | null | undefined;
};

interface ISignalRContext {
  connection: HubConnection | null;
  addMessage: (message: Omit<SignalRMessage, "id" | "timestamp">) => void;
  messages: SignalRMessage[];
  removeMessage: (id?: string) => void;
}

const SignalRContext = createContext<ISignalRContext | undefined>(undefined);

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }
  return context;
};

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [messages, setMessages] = useState<SignalRMessage[]>([]);
  const hub = useSelector((x: AppState) => x.fleetManagerHub.hub);

  const addMessage = useCallback(
    (message: Omit<SignalRMessage, "id" | "timestamp">) => {
      const newMessage: SignalRMessage = {
        ...message,
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
      };
      setMessages((prev) => {
        const updated = [...prev, newMessage];
        return updated;
      });
    },
    []
  );

  const removeMessage = (id?: string) => {
    if (id) {
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    }
  };

  const refresh = useRefresh();

  useEffect(() => {
    if (!hub) {
      return;
    }

    const subscribeToDataTransferUpdates = () => {
      hub.invoke("SubscribeToDataTransferExportUpdates").catch(() => undefined);
      hub.invoke("SubscribeToDataTransferImportUpdates").catch(() => undefined);
    };

    const dispatchDataTransferEvent = (eventName: string, detail: unknown) => {
      if (typeof window === "undefined") {
        return;
      }

      window.dispatchEvent(new CustomEvent(eventName, { detail }));
    };

    const handleReportFinished = (data: ReportResultDto) => {
      hub
        .invoke("UnsubscribeFromReportUpdates", data.reportId)
        .catch((e) => console.error(e));
      if (data.success && data.reportUrl) {
        addMessage({
          type: SignalRTypeEnum.Report,
          status: SignalRStatusEnum.Success,
          reportUrl: data.reportUrl,
          machineName: data.name,
        });
      } else {
        addMessage({
          type: SignalRTypeEnum.Report,
          status: SignalRStatusEnum.Error,
        });
      }
    };

    const handleEldFileExportStatusChanged = (data: EldFileExportDto) => {
      if (
        Number(data.status) === ExportTypes.Succeeded ||
        Number(data.status) === ExportTypes.Failed
      ) {
        addMessage({
          type: SignalRTypeEnum.Export,
          status:
            Number(data.status) === ExportTypes.Succeeded
              ? SignalRStatusEnum.Success
              : SignalRStatusEnum.Error,
          exportUrl: data.downloadUrl ?? undefined,
        });
        hub
          .invoke("UnsubscribeFromSingleEldFileExportUpdates", data.id)
          .catch((e) => console.error(e));
        if (window.location.pathname.includes("eld-export")) {
          refresh();
        }
      }
    };

    const handleDataTransferExportStatusChanged = (data: unknown) => {
      dispatchDataTransferEvent("prologs:data-transfer-export-status", data);
    };

    const handleDataTransferImportStatusChanged = (data: unknown) => {
      dispatchDataTransferEvent("prologs:data-transfer-import-status", data);
    };

    const handleHubReconnected = () => {
      subscribeToDataTransferUpdates();
    };

    subscribeToDataTransferUpdates();

    hub.on("NotifyReportFinished", handleReportFinished);
    hub.on("OnEldFileExportStatusChanged", handleEldFileExportStatusChanged);
    hub.on(
      "OnDataTransferExportStatusChanged",
      handleDataTransferExportStatusChanged
    );
    hub.on(
      "OnDataTransferImportStatusChanged",
      handleDataTransferImportStatusChanged
    );
    hub.onreconnected(handleHubReconnected);

    return () => {
      hub.off("NotifyReportFinished", handleReportFinished);
      hub.off("OnEldFileExportStatusChanged", handleEldFileExportStatusChanged);
      hub.off(
        "OnDataTransferExportStatusChanged",
        handleDataTransferExportStatusChanged
      );
      hub.off(
        "OnDataTransferImportStatusChanged",
        handleDataTransferImportStatusChanged
      );
      const connection = hub as any;
      if (typeof connection.offreconnected === "function") {
        connection.offreconnected(handleHubReconnected);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hub, addMessage, refresh]);

  return (
    <SignalRContext.Provider
      value={{ connection: hub ?? null, addMessage, messages, removeMessage }}
    >
      {children}
    </SignalRContext.Provider>
  );
};
